import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchIncidentDashboard } from '../../../features/incidents/incidentActions';
import { localDate, monthRange } from '../../../components/dateRange';
import { complianceArea, incidentActivity, incidentAge, incidentEvidence, incidentsCsv, isOpen, summarizeIncidentDashboard } from '../incidentData';

const IncidentContext = createContext(null);
const EMPTY_FILTERS = { search: '', status: '', type: '', location: '', category: '', assignee: '', olderThan7: false };
const PAGE_LIMIT = 8;

export function IncidentProvider({ children }) {
  const dispatch = useDispatch();
  const [range, setRange] = useState(monthRange);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [panel, setPanel] = useState(null);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const dashboard = useSelector(state => state.incidents.dashboard);
  const mutationVersion = useSelector(state => state.incidents.mutationVersion);
  const today = localDate();
  useEffect(() => {
    const request = dispatch(fetchIncidentDashboard(range));
    return () => request.abort();
  }, [dispatch, range, mutationVersion]);
  useEffect(() => { setPage(1); }, [range, filters]);
  const data = dashboard.data?.range.startDate === range.startDate && dashboard.data?.range.endDate === range.endDate ? dashboard.data : null;
  const records = useMemo(() => data?.records || [], [data]);
  const loading = dashboard.loading || (!dashboard.error && !data);
  const incidents = useMemo(() => records.filter(row => {
    const search = filters.search.trim().toLowerCase();
    return (!search || [row.description, row.location, row.type, row.correctiveAction, row.createdBy?.fullName, `INC-${String(row.id).padStart(4, '0')}`].some(value => String(value || '').toLowerCase().includes(search))) &&
      (!filters.status || (filters.status === 'ACTIVE' ? isOpen(row) : row.status === filters.status)) &&
      (!filters.type || row.type === filters.type) && (!filters.location || row.location === filters.location) &&
      (!filters.category || complianceArea(row) === filters.category) &&
      (!filters.assignee || (filters.assignee === 'none' ? !row.referredToId && !row.referredTo?.id : String(row.referredToId || row.referredTo?.id) === filters.assignee)) &&
      (!filters.olderThan7 || (isOpen(row) && incidentAge(row, today) > 7));
  }), [records, filters, today]);
  const summary = useMemo(() => summarizeIncidentDashboard(incidents, today), [incidents, today]);
  const documents = useMemo(() => incidentEvidence(incidents), [incidents]);
  const activity = useMemo(() => incidentActivity(incidents), [incidents]);
  const actions = incidents.filter(row => isOpen(row) && row.correctiveAction?.trim());
  const totalPages = Math.ceil(incidents.length / PAGE_LIMIT);
  const currentPage = Math.min(page, totalPages || 1);
  const pageIncidents = incidents.slice((currentPage - 1) * PAGE_LIMIT, currentPage * PAGE_LIMIT);
  const updateFilter = (name, value) => setFilters(previous => ({ ...previous, [name]: value }));
  const focusInventory = () => document.getElementById('incident-inventory')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const showOpen = (olderThan7 = false) => { setFilters(previous => ({ ...previous, status: 'ACTIVE', olderThan7 })); focusInventory(); };
  const exportIncidents = () => {
    const url = URL.createObjectURL(new Blob([incidentsCsv(incidents)], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `incidents-${range.startDate}-to-${range.endDate}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const emptyMessage = text => loading ? 'Loading incidents…' : dashboard.error ? 'Unable to load incidents.' : text;
  return <IncidentContext.Provider value={{ range, setRange, records, incidents, pageIncidents, summary, documents, activity, actions, loading,
    error: dashboard.error, filters, setFilters, updateFilter, filtersOpen, setFiltersOpen, currentPage, totalPages, pageLimit: PAGE_LIMIT, setPage,
    resetFilters: () => setFilters(EMPTY_FILTERS), panel, setPanel, selectedIncident, setSelectedIncident, showOpen, exportIncidents, emptyMessage, today }}>
    {children}
  </IncidentContext.Provider>;
}

export function useIncident() {
  const context = useContext(IncidentContext);
  if (!context) throw new Error('useIncident must be used inside IncidentProvider');
  return context;
}
export default IncidentContext;
